import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const coingeckoURL = "https://api.coingecko.com/api/v3";

// App tickers -> CoinGecko coin ids
const COIN_ID_MAP: Record<string, string> = {
  BTC: "bitcoin",
  USDT: "tether",
  ETH: "ethereum",
  DOGE: "dogecoin",
  SHIB: "shiba-inu",
  SOL: "solana",
  TRX: "tron",
  USDC: "usd-coin",
  BNB: "binancecoin",
  POL: "matic-network",
};

// The "NGN" wallet shows the USD value in Naira; proxy USD with a stablecoin.
const NGN_PROXY_ID = "tether";

// CoinGecko's public (no-key) API caps historical data at the past 365 days,
// so "ALL" is clamped to 365 to avoid the 10012 "exceeds time range" error.
const rangeToDays: Record<string, number> = {
  "1D": 1,
  "1W": 7,
  "1M": 30,
  "1Y": 365,
  ALL: 365,
};

const getCoinId = (crypto: string) =>
  crypto === "NGN"
    ? NGN_PROXY_ID
    : COIN_ID_MAP[crypto?.toUpperCase()] ?? "bitcoin";

// Mimic CryptoCompare's DISPLAY formatting (more decimals for sub-dollar coins).
const formatNumber = (value: number) => {
  const abs = Math.abs(value);
  const decimals = abs >= 1 ? 2 : abs >= 0.01 ? 4 : 8;
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const getCryptoData = async (
  crypto: string,
  range: "1D" | "1W" | "1M" | "1Y" | "ALL"
) => {
  const isNGN = crypto === "NGN";
  const id = getCoinId(crypto);
  const vs = isNGN ? "ngn" : "usd";
  const days = rangeToDays[range] ?? 7;

  const response = await axios.get(
    `${coingeckoURL}/coins/${id}/market_chart?vs_currency=${vs}&days=${days}`
  );

  const prices: [number, number][] = response.data?.prices ?? [];

  const trades = prices.map(([time, price]) => ({
    price: price ?? 0,
    time: Math.floor(time / 1000),
  }));

  return trades;
};

export const getCryptoInfo = async (crypto: string) => {
  try {
    const isNGN = crypto === "NGN";
    const id = getCoinId(crypto);
    const vs = isNGN ? "ngn" : "usd";

    const response = await axios.get(
      `${coingeckoURL}/coins/markets?vs_currency=${vs}&ids=${id}`
    );

    const coin = response.data?.[0];
    if (!coin) return undefined;

    const price = coin.current_price ?? 0;
    const change24Hour = coin.price_change_24h ?? 0;
    const changePct24Hour = coin.price_change_percentage_24h ?? 0;

    const symbol = isNGN ? "\u20A6" : "$";
    const display = {
      PRICE: `${symbol} ${formatNumber(price)}`,
      CHANGE24HOUR: `${symbol} ${formatNumber(change24Hour)}`,
      CHANGEPCT24HOUR: changePct24Hour.toFixed(2),
    };

    // Reshape to the CryptoCompare structure the UI already reads.
    const DISPLAY: Record<string, Record<string, typeof display>> = isNGN
      ? { USD: { NGN: display } }
      : { [crypto.toUpperCase()]: { USD: display } };

    return { DISPLAY };
  } catch (error) {
    // console.error(error);
    return undefined;
  }
};

const cryptoData = async (
  crypto: string,
  range: "1D" | "1W" | "1M" | "1Y" | "ALL"
) => {
  const allData = await Promise.all([
    getCryptoData(crypto, range),
    getCryptoInfo(crypto),
  ]);
  return {
    marketData: allData[0],
    coinData: allData[1],
  };
};

const useCryptoData = (crypto: string, range: "1D" | "1W" | "1M" | "1Y" | "ALL") =>
  useQuery({
    queryKey: ["crypto-data", crypto, range],
    queryFn: () => cryptoData(crypto, range),
    // Cache each range so toggling between already-loaded ranges doesn't refetch,
    // which keeps us under CoinGecko's public API rate limit.
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // Back off on 429 (rate limit) instead of failing immediately.
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30_000),
  });

export { useCryptoData };
